import type { Folder } from '@newfront-insurance/files-api';
import { filenamify } from '@newfront-insurance/files-api';

import { EML_CONTENT_TYPE, MSG_CONTENT_TYPE } from '../bff/trpc/lib/content-types';
import type { File } from '../bff/types';
import { isYearOnlyNewFolder } from '../client/features/folders-tree/helpers';

/**
 * @param filename the full filename
 * @returns the filename extension like `csv` or `pdf`
 */
export function getExtension(filename: string | undefined, isEmailLogger = false): string | undefined {
  if (!filename || isEmailLogger) {
    return undefined;
  }

  const fileParts = filename.split('.');

  if (fileParts.length === 1) {
    return undefined;
  }

  const extension = fileParts.pop()?.replace(/[^a-zA-Z0-9]/g, '');

  return extension && extension.length < 5 ? extension : undefined;
}

/**
 * @param filename the full filename
 * @returns the filename without the extension
 */
export function stripExtension(filename: string): string {
  const fileParts = filename.split('.');

  if (fileParts.length === 1) {
    return filename;
  }

  return fileParts.slice(0, -1).join('.');
}

/**
 * @param contentLength the content length in bytes
 * @returns readable content length like "1.2 MB"
 */
export function getReadableFilesize(contentLength: string): string {
  if (!contentLength) {
    return '0 B';
  }

  const bytes = parseInt(contentLength, 10);
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / 1024 ** i).toFixed(0)} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
}

export function isKnownExtension(extension: string | undefined): boolean {
  const commonExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'ico',
    'pdf',
    'doc',
    'docx',
    'txt',
    'rtf',
    'odt',
    'xls',
    'xlsm',
    'xlsx',
    'ods',
    'csv',
    'ppt',
    'pptx',
    'odp',
    'mp3',
    'wav',
    'ogg',
    'flac',
    'mp4',
    'avi',
    'mov',
    'wmv',
    'mkv',
    'zip',
    'rar',
    'tar',
    'gz',
    '7z',
  ];

  return commonExtensions.includes(extension?.toLowerCase() ?? '');
}

export function replaceFilenameInvalidChars(str: string | undefined): string | undefined {
  if (!str) return;
  return filenamify(str);
}

/**
 * Files created via e-mail logger doesn't have an extension and that causes issues when downloading
 * We need to fix the filename by adding the .eml extension manually
 *
 * @param file The file from the api
 * @param customFilename custom filename provided by the user
 * @returns the filename with the .emle xtension
 */
export function patchFilename(file: File, customFilename?: string): string | undefined {
  const filename = replaceFilenameInvalidChars(customFilename ?? file.filename);
  const filenameWithoutExtension = stripExtension(filename ?? '');

  if (file.isEmailLogger || file.contentType === EML_CONTENT_TYPE) {
    return `${filenameWithoutExtension}.eml`;
  }

  if (file.contentType === MSG_CONTENT_TYPE) {
    return `${filenameWithoutExtension}.msg`;
  }

  return filename;
}

// OS-specific duplicate suffix patterns (applied to the filename stem, without extension)
const WINDOWS_SUFFIX = /\s\(\d+\)/; // "file (1)", "file (2)"
const MACOS_SUFFIX = /\scopy(?:\s\d+)?/; // "file copy", "file copy 2"
const LINUX_SUFFIX = /\s\((?:(?:another|\d+(?:th|st|nd|rd))\s)?copy\)/; // "file (copy)", "file (2nd copy)"

const OS_DUPLICATE_SUFFIX = new RegExp(
  `(?:${WINDOWS_SUFFIX.source}|${MACOS_SUFFIX.source}|${LINUX_SUFFIX.source})$`,
  'i',
);

/**
 * Strips OS-generated duplicate suffixes from a filename.
 *
 * Examples:
 *  - "cert (1).pdf"        → "cert.pdf"
 *  - "cert copy.pdf"       → "cert.pdf"
 *  - "cert copy 2.pdf"     → "cert.pdf"
 *  - "cert (2nd copy).pdf" → "cert.pdf"
 *  - "cert.pdf"            → "cert.pdf" (unchanged)
 */
export function normalizeFilename(filename: string): string {
  const stem = stripExtension(filename);
  const ext = filename.slice(stem.length);

  const normalizedStem = stem.replace(OS_DUPLICATE_SUFFIX, '');
  return normalizedStem + ext;
}

export function sortFoldersFunction(a: Folder, b: Folder) {
  if (isYearOnlyNewFolder(a.parentFolderType) && isYearOnlyNewFolder(b.parentFolderType)) {
    return Number(b.displayName) - Number(a.displayName);
  }
  return a.displayName.localeCompare(b.displayName);
}
